import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PropertyInputs, CalculationResults } from '../types';
import { formatValue } from './formatters';

export const exportLOIToPDF = async (
  previewContent: string,
  sender: string,
  recipient: string,
  date: string,
  amount: number
) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  // Set font
  pdf.setFontSize(10);
  
  // Add sender
  const senderLines = sender.split('\n');
  let y = 20;
  senderLines.forEach(line => {
    pdf.text(line, 20, y);
    y += 5;
  });
  
  // Add recipient
  y += 10;
  const recipientLines = recipient.split('\n');
  recipientLines.forEach(line => {
    pdf.text(line, 20, y);
    y += 5;
  });
  
  // Add date
  y += 10;
  pdf.text(date, 150, y);
  
  // Add amount
  y += 15;
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.text(`Angebotsbetrag: ${formatValue(amount, '€')}`, 20, y);
  
  // Add content
  y += 10;
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(10);
  
  const contentLines = previewContent.split('\n');
  contentLines.forEach(line => {
    if (y > 270) {
      pdf.addPage();
      y = 20;
    }
    
    // Handle long lines
    const splitLines = pdf.splitTextToSize(line, 170);
    if (Array.isArray(splitLines)) {
      splitLines.forEach(splitLine => {
        pdf.text(splitLine, 20, y);
        y += 5;
      });
    } else {
      pdf.text(splitLines, 20, y);
      y += 5;
    }
  });
  
  // Save the PDF
  pdf.save(`Angebotschreiben_${new Date().toISOString().split('T')[0]}.pdf`);
};

export const exportReportToPDF = async (elementId: string, filename: string = 'Report') => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  // Store original styles
  const originalOverflow = element.style.overflow;
  const originalHeight = element.style.height;
  const originalMaxHeight = element.style.maxHeight;
  
  // Temporarily modify element to capture full content
  element.style.overflow = 'visible';
  element.style.height = 'auto';
  element.style.maxHeight = 'none';
  
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    pdf.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  } finally {
    // Restore original styles
    element.style.overflow = originalOverflow;
    element.style.height = originalHeight;
    element.style.maxHeight = originalMaxHeight;
  }
};

export const generateDetailedReport = (
  inputs: PropertyInputs,
  calculations: CalculationResults
) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  let y = 20;
  
  // Title
  pdf.setFontSize(18);
  pdf.setFont(undefined, 'bold');
  pdf.text('Immobilien Investment Report', 105, y, { align: 'center' });
  
  // Date
  y += 10;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 105, y, { align: 'center' });
  
  // Property Overview
  y += 20;
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text('Objektübersicht', 20, y);
  
  y += 10;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  const objectData = [
    ['Objektname:', inputs.propertyName || 'Nicht angegeben'],
    ['Stadtgebiet:', inputs.stadtgebiet || 'Nicht angegeben'],
    ['Wohnfläche:', `${inputs.wohnflaeche || 0} m²`],
    ['Baujahr:', inputs.baujahr || 'Nicht angegeben'],
    ['Angebotspreis:', formatValue(inputs.angebotspreis || 0, '€')]
  ];
  
  objectData.forEach(([label, value]) => {
    pdf.setFont(undefined, 'bold');
    pdf.text(label, 20, y);
    pdf.setFont(undefined, 'normal');
    pdf.text(value, 60, y);
    y += 6;
  });
  
  // Financial KPIs
  y += 10;
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text('Finanzielle Kennzahlen', 20, y);
  
  y += 10;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  const kpiData = [
    ['Eigenkapitalrendite:', formatValue(calculations.eigenkapitalrendite || 0, '%')],
    ['Bruttomietrendite IST:', formatValue(calculations.bruttomietrenditeIst || 0, '%')],
    ['Bruttomietrendite SOLL:', formatValue(calculations.bruttomietrenditeSoll || 0, '%')],
    ['Kaufpreisfaktor:', formatValue(calculations.kaufpreisFaktor || 0)],
    ['Monatl. Cashflow Jahr 1:', formatValue(calculations.monatlicherCashflowJahr1SollNachSteuer || 0, '€')]
  ];
  
  kpiData.forEach(([label, value]) => {
    pdf.setFont(undefined, 'bold');
    pdf.text(label, 20, y);
    pdf.setFont(undefined, 'normal');
    pdf.text(value, 70, y);
    y += 6;
  });
  
  // Add new page for projections if needed
  if (y > 240) {
    pdf.addPage();
    y = 20;
  }
  
  // Projections
  y += 10;
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text('Projektionen', 20, y);
  
  y += 10;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  
  if (calculations.projectionData && calculations.projectionData.length > 0) {
    const years = [6, 11, 20];
    years.forEach(year => {
      if (calculations.projectionData[year]) {
        const data = calculations.projectionData[year];
        pdf.text(`Jahr ${year}:`, 20, y);
        y += 5;
        pdf.text(`  - Nettovermögen: ${formatValue(data.vermoegen || 0, '€')}`, 25, y);
        y += 5;
        pdf.text(`  - Monatl. Cashflow: ${formatValue(data.monatlicherCashflowNachSteuer || 0, '€')}`, 25, y);
        y += 7;
      }
    });
  }
  
  // Save
  pdf.save(`Investment_Report_${inputs.propertyName || 'Immobilie'}_${new Date().toISOString().split('T')[0]}.pdf`);
};